import React, { useCallback, useEffect, useRef, useState } from 'react'

/* The self view used to be nailed to the stage's top-right corner, where it covers
   whatever is drawn underneath it. This wrapper lets the user drag it anywhere on the
   screen instead.

   Three details that matter:
   - position:fixed, not absolute. The stage (.bn-main) is overflow:hidden, so an
     absolutely placed card would be clipped at the stage edges and could never travel
     over the top bar or the control bar. No ancestor sets a transform, so fixed really
     does resolve against the viewport here.
   - pointer events + setPointerCapture, so mouse, touch and pen all behave the same and
     a fast drag that outruns the cursor does not drop the card mid-move. Without capture
     the pointer would land on the canvas and start drawing a stroke.
   - every position goes through clamp(), on drag and on resize alike, so the card can
     never be parked off-screen where there is nothing left to grab. */

const MARGIN = 12

/* Kept outside the component on purpose: the card unmounts whenever the camera is
   toggled or the board closes, and remounting it back in the corner after the user had
   deliberately moved it reads as the app undoing their action. */
let lastPos = null

export default function DraggablePip({ className = '', children, ...rest }) {
    const nodeRef = useRef(null)
    const dragRef = useRef(null)
    const [pos, setPos] = useState(lastPos)
    const [dragging, setDragging] = useState(false)

    const clamp = useCallback((left, top) => {
        const el = nodeRef.current
        const w = el ? el.offsetWidth : 0
        const h = el ? el.offsetHeight : 0
        const maxLeft = Math.max(MARGIN, window.innerWidth - w - MARGIN)
        const maxTop = Math.max(MARGIN, window.innerHeight - h - MARGIN)
        return {
            left: Math.min(Math.max(left, MARGIN), maxLeft),
            top: Math.min(Math.max(top, MARGIN), maxTop),
        }
    }, [])

    const place = useCallback((next) => {
        lastPos = next
        setPos(next)
    }, [])

    /* On the first paint the card is still where the stylesheet put it. Read that spot and
       re-express it as viewport coordinates, so handing over to position:fixed does not
       move a single pixel. */
    useEffect(() => {
        const el = nodeRef.current
        if (!el) return
        if (lastPos) { place(clamp(lastPos.left, lastPos.top)); return }
        const r = el.getBoundingClientRect()
        place(clamp(r.left, r.top))
    }, [clamp, place])

    useEffect(() => {
        const onResize = () => {
            if (!lastPos) return
            place(clamp(lastPos.left, lastPos.top))
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [clamp, place])

    const onPointerDown = (e) => {
        if (e.button != null && e.button !== 0) return
        const el = nodeRef.current
        if (!el) return
        const r = el.getBoundingClientRect()
        dragRef.current = {
            pointerId: e.pointerId,
            offsetX: e.clientX - r.left,
            offsetY: e.clientY - r.top,
        }
        place({ left: r.left, top: r.top })
        setDragging(true)
        try { el.setPointerCapture(e.pointerId) } catch (err) { /* capture is best effort */ }
        e.preventDefault()
        e.stopPropagation()
    }

    const onPointerMove = (e) => {
        const d = dragRef.current
        if (!d || d.pointerId !== e.pointerId) return
        place(clamp(e.clientX - d.offsetX, e.clientY - d.offsetY))
        e.preventDefault()
        e.stopPropagation()
    }

    const endDrag = (e) => {
        const d = dragRef.current
        if (!d || (e && e.pointerId != null && d.pointerId !== e.pointerId)) return
        dragRef.current = null
        setDragging(false)
        const el = nodeRef.current
        try { if (el && el.hasPointerCapture(d.pointerId)) el.releasePointerCapture(d.pointerId) } catch (err) { /* already released */ }
    }

    return (
        <div ref={nodeRef}
             className={`${className} bn-pip-draggable${dragging ? ' bn-pip-dragging' : ''}`}
             style={pos ? { position: 'fixed', left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' } : undefined}
             onPointerDown={onPointerDown}
             onPointerMove={onPointerMove}
             onPointerUp={endDrag}
             onPointerCancel={endDrag}
             onLostPointerCapture={endDrag}
             {...rest}>
            {children}
        </div>
    )
}
